import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LumioNudge } from '../LumioChat/endpoint';
import { Toast, Head, Mark, Noticed, Close, Message, Actions, ActionChip } from './NudgeToast.styles';

/** How long the floating toast lingers before it self-dismisses as `ignored`. The
 * toast is an interruption near the launcher (shown only when every Lumio surface
 * is closed), so it clears itself if untouched — unlike the docked "Noticed" card,
 * which persists while the owner is on the page. */
const TOAST_DISMISS_MS = 20_000;

interface NudgeToastProps {
  nudge: LumioNudge;
  /** Send the nudge's action message into Lumio as a real chat turn. */
  onAct: (message: string) => void;
  onDismiss: () => void;
  /** Called when the toast self-dismisses without interaction (a soft "not now"). */
  onTimeout: () => void;
}

export interface Rendered {
  message: string;
  action?: { label: string; message: string };
}

/**
 * Adapt a nudge to the shape the toast / docked card renders. The copy is
 * BACKEND-rendered and already localized (lumio `proactive_copy.py`): `message`
 * plus, when the nudge carries an action, `action_label` + `action_message`.
 * This just maps those fields — the frontend holds no nudge copy. A nudge with
 * no `message` (unknown id server-side) renders nothing.
 */
export const renderNudge = (nudge: LumioNudge): Rendered | null => {
  if (!nudge.message) return null;
  return {
    message: nudge.message,
    action:
      nudge.action && nudge.action_label && nudge.action_message
        ? { label: nudge.action_label, message: nudge.action_message }
        : undefined,
  };
};

/**
 * The visible face of a proactive nudge: a small Lumio card near the assistant
 * launcher. Its action chip is the on-ramp — clicking it opens Lumio and asks
 * the question, turning a passive notice into a real conversation.
 */
export const NudgeToast = ({ nudge, onAct, onDismiss, onTimeout }: NudgeToastProps): React.ReactElement | null => {
  const { t } = useTranslation();

  // Self-dismiss after a beat if untouched. Keyed on `nudge` so a fresh nudge
  // restarts the clock; `onTimeout` is read through a ref so re-renders don't
  // reset the timer (it would otherwise never fire with an inline callback).
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  useEffect(() => {
    const id = window.setTimeout(() => onTimeoutRef.current(), TOAST_DISMISS_MS);
    return () => window.clearTimeout(id);
  }, [nudge]);

  const rendered = renderNudge(nudge);
  if (!rendered) return null;
  return (
    <Toast role="status" aria-live="polite">
      <Head>
        <Mark>Lumio</Mark>
        <Noticed>{t('noticed something')}</Noticed>
        <Close type="button" aria-label={t('Dismiss')} onClick={onDismiss}>
          ×
        </Close>
      </Head>
      <Message>{rendered.message}</Message>
      {nudge.action && rendered.action && (
        <Actions>
          <ActionChip
            type="button"
            onClick={() => {
              onAct(rendered.action!.message);
            }}
          >
            {rendered.action.label}
          </ActionChip>
        </Actions>
      )}
    </Toast>
  );
};
